import { z } from 'zod'
import { emptyStringValueToNull } from 'shared/lib/validation'
import { ADDRESS_LINE_MAX_LENGTH, ADDRESS_STREET_MAX_LENGTH } from 'shared/ncts/common/constants'

export const ContactPersonSchema = z.object({
  id: z.number().nullable(),
  deleted: z.boolean().optional(),
  name: z.string(),
  email: z.string().email().or(z.literal('')),
  telephone: z.string(),
})
export type ContactPerson = z.infer<typeof ContactPersonSchema>

export const AddressSchema = z.object({
  id: z.number().nullable(),
  addressLine: z.string().max(ADDRESS_LINE_MAX_LENGTH).optional(),
  street: z.string().max(ADDRESS_STREET_MAX_LENGTH).optional(),
  streetNumber: z.string().optional(),
  postcode: z.string(),
  city: z.string(),
  country: z.string(),
})
export type AddressFormValues = z.infer<typeof AddressSchema>

export const BooleanSchema = z.enum(['true', 'false']).nullable()
export type BooleanType = z.infer<typeof BooleanSchema>

export const DepartureTransportMeansSchema = z.object({
  id: z.number().nullable(),
  deleted: z.boolean(),
  sequenceNumber: z.number().nonnegative(),
  typeOfIdentification: z.string(),
  identificationNumber: z.string(),
  nationality: z.string(),
})
export type DepartureTransportMeansType = z.infer<typeof DepartureTransportMeansSchema>

export const CountriesOfRoutingSchema = z.object(
  {
    id: z.number().nullable(),
    sequenceNumber: z.number().nonnegative(),
    country: z.string().nullable(),
    deleted: z.boolean().optional(),
  },
)
export type CountriesOfRoutingType = z.infer<typeof CountriesOfRoutingSchema>

export const ActiveBorderTransportMeansSchema = z.object(
  {
    id: z.number().nullable(),
    sequenceNumber: z.number().nonnegative(),
    customsOfficeAtBorderReferenceNumber: z.string(),
    typeOfIdentification: z.string(),
    typeOfMeansOfTransport: z.string().nullable(),
    identificationNumber: z.string(),
    nationality: z.string(),
    conveyanceReferenceNumber: z.string().nullable(),
    actualDateAndTimeOfDeparture: emptyStringValueToNull(z.string().datetime({ local: true })).or(z.literal('')),
    estimatedDateAndTimeOfDeparture: emptyStringValueToNull(z.string().datetime({ local: true })).or(z.literal('')),
    estimatedDateAndTimeOfArrival: emptyStringValueToNull(z.string().datetime({ local: true })).or(z.literal('')),
    deleted: z.boolean().optional(),
    countriesOfRouting: z.array(CountriesOfRoutingSchema),
  },
)
export type ActiveBorderTransportMeansType = z.infer<typeof ActiveBorderTransportMeansSchema>

export const AdditionalReferenceSchema = z.object(
  {
    id: z.number().nullable(),
    sequenceNumber: z.number().nonnegative(),
    type: z.string(),
    referenceNumber: z.string(),
    deleted: z.boolean().optional(),
  },
)
export type AdditionalReferenceFormValues = z.infer<typeof AdditionalReferenceSchema>

export const AdditionalInformationSchema = z.object(
  {
    id: z.number().nullable(),
    sequenceNumber: z.number().nonnegative(),
    code: z.string(),
    text: z.string(),
    deleted: z.boolean().optional(),
  },
)
export type AdditionalInformationType = z.infer<typeof AdditionalInformationSchema>

export const CustomsOfficeOfTransitDeclaredSchema = z.object(
  {
    id: z.number().nullable(),
    sequenceNumber: z.number().nonnegative(),
    referenceNumber: z.string(),
    arrivalDateAndTime: emptyStringValueToNull(z.string().datetime({ local: true })).or(z.literal('')),
    entryIndicator: BooleanSchema.default('false'),
    reEntryIndicator: BooleanSchema.default('false'),
    deleted: z.boolean().optional(),
  },
)
export type CustomsOfficeOfTransitDeclared = z.infer<typeof CustomsOfficeOfTransitDeclaredSchema>

export const CustomsOfficeOfExitForTransitDeclaredSchema = z.object(
  {
    id: z.number().nullable(),
    sequenceNumber: z.number().nonnegative(),
    referenceNumber: z.string(),
    deleted: z.boolean().optional(),
  },
)
export type CustomsOfficeOfExitForTransitDeclared = z.infer<typeof CustomsOfficeOfExitForTransitDeclaredSchema>

export const AuthorisationSchema = z.object(
  {
    id: z.number().nullable(),
    sequenceNumber: z.number().nonnegative(),
    type: z.string(),
    referenceNumber: z.string(),
    deleted: z.boolean().optional(),
  },
)
export type AuthorisationFormValues = z.infer<typeof AuthorisationSchema>

const SealSchema = z.object({
  id: z.number().nullable(),
  sequenceNumber: z.number().nonnegative(),
  identifier: z.string(),
  deleted: z.boolean().optional(),
})

const GoodsReferencesSchema = z.object({
  id: z.number().nullable(),
  sequenceNumber: z.number().nonnegative(),
  // @deprecated
  declarationGoodsItemNumber: z.number(),
  consignmentItemId: z.number().nullable(),
  deleted: z.boolean().optional(),
})

export const TransportEquipmentSchema = z.object({
  id: z.number().nullable(),
  sequenceNumber: z.number().nonnegative(),
  containerIdentificationNumber: z.string(),
  containerSizeAndType: z.string().nullable(),
  packedStatus: z.string().nullable(),
  supplierType: z.string().nullable(),
  seals: z.array(SealSchema),
  goodsReferences: z.array(GoodsReferencesSchema),
  deleted: z.boolean().optional(),
})

export const PassiveBorderTransportMeansSchema = z.object(
  {
    id: z.number().nullable(),
    deleted: z.boolean().optional(),
    sequenceNumber: z.number().nonnegative(),
    identificationNumber: z.string(),
    typeOfIdentification: z.string(),
    typeOfMeansOfTransport: z.string().nullable(),
    nationality: z.string(),
  },
)
export type PassiveBorderTransportMeansType = z.infer<typeof PassiveBorderTransportMeansSchema>

export type SealType = z.infer<typeof SealSchema>
export type GoodsReferenceType = z.infer<typeof GoodsReferencesSchema>
export type TransportEquipmentType = z.infer<typeof TransportEquipmentSchema>
